import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';
import { generateGameplan } from '../utils/gameplanGenerator';
import { generatePDF } from '../utils/pdfGenerator';

const { 
  FiLogOut, FiUsers, FiFileText, FiDownload, FiEye, 
  FiTrash2, FiSearch, FiFilter, FiRefreshCw, FiCalendar,
  FiTrendingUp, FiDollarSign
} = FiIcons;

function AdminDashboard() {
  const [gameplans, setGameplans] = useState([]);
  const [filteredGameplans, setFilteredGameplans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedGameplan, setSelectedGameplan] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    draft: 0,
    thisMonth: 0
  });
  
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
    } else {
      fetchGameplans();
    }
  }, [user, navigate]);

  useEffect(() => {
    filterGameplans();
  }, [searchTerm, statusFilter, gameplans]);

  const fetchGameplans = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('gameplans')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setGameplans(data || []);
      calculateStats(data || []);
    } catch (error) {
      console.error('Error fetching gameplans:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const now = new Date();
    const thisMonth = data.filter(gp => {
      const created = new Date(gp.created_at);
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length;

    setStats({
      total: data.length,
      completed: data.filter(gp => gp.status === 'completed').length,
      draft: data.filter(gp => gp.status === 'draft').length,
      thisMonth
    });
  };

  const filterGameplans = () => {
    let filtered = [...gameplans];

    if (searchTerm) {
      filtered = filtered.filter(gp => 
        gp.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        gp.client_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(gp => gp.status === statusFilter);
    }

    setFilteredGameplans(filtered);
  };

  const handleViewGameplan = (gameplan) => {
    setSelectedGameplan(gameplan);
  };

  const handleDownloadPDF = async (gameplan) => {
    try {
      await generatePDF(gameplan.gameplan_data);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDeleteGameplan = async (id) => {
    if (!confirm('Are you sure you want to delete this game plan?')) return;

    try {
      const { error } = await supabase
        .from('gameplans')
        .delete()
        .eq('id', id);

      if (error) throw error;

      fetchGameplans();
    } catch (error) {
      console.error('Error deleting game plan:', error);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Manage client game plans</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={fetchGameplans}
                className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiRefreshCw} />
                Refresh
              </button>
              <button
                onClick={handleSignOut}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <SafeIcon icon={FiLogOut} />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Game Plans</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiFileText} className="text-2xl text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Completed</p>
                <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="text-2xl text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">In Progress</p>
                <p className="text-3xl font-bold text-orange-600">{stats.draft}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiUsers} className="text-2xl text-orange-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">This Month</p>
                <p className="text-3xl font-bold text-purple-600">{stats.thisMonth}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiCalendar} className="text-2xl text-purple-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('completed')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'completed'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Completed
              </button>
              <button
                onClick={() => setStatusFilter('draft')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'draft'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Draft
              </button>
            </div>
          </div>
        </div>

        {/* Game Plans Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredGameplans.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No game plans found
                    </td>
                  </tr>
                ) : (
                  filteredGameplans.map((gameplan) => (
                    <tr key={gameplan.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{gameplan.client_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{gameplan.client_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          gameplan.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : gameplan.status === 'draft'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {gameplan.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {format(new Date(gameplan.created_at), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewGameplan(gameplan)}
                            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <SafeIcon icon={FiEye} />
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(gameplan)}
                            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
                            title="Download PDF"
                          >
                            <SafeIcon icon={FiDownload} />
                          </button>
                          <button
                            onClick={() => handleDeleteGameplan(gameplan.id)}
                            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Game Plan Modal */}
      {selectedGameplan && (
        <GamePlanModal 
          gameplan={selectedGameplan} 
          onClose={() => setSelectedGameplan(null)}
          onDownload={handleDownloadPDF}
        />
      )}
    </div>
  );
}

function GamePlanModal({ gameplan, onClose, onDownload }) {
  const data = gameplan.gameplan_data;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{gameplan.client_name}'s Game Plan</h2>
            <p className="text-sm text-gray-600 mt-1">{gameplan.client_email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Five Ones */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">The Rule of 5 Ones</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Target Market</h4>
                <p className="text-gray-700">{data.fiveOnes.targetMarket}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">Offer</h4>
                <p className="text-gray-700">{data.fiveOnes.offer}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-800 mb-2">Conversion Mechanism</h4>
                <p className="text-gray-700">{data.fiveOnes.conversionMechanism}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-2">Price Point</h4>
                <p className="text-gray-700">${data.fiveOnes.pricePoint}</p>
              </div>
            </div>
          </section>

          {/* Revenue Goals */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Revenue Targets</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Monthly Goal</h4>
                <p className="text-2xl font-bold text-gray-900">${data.revenue.monthlyGoal?.toLocaleString()}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Daily Traffic</h4>
                <p className="text-2xl font-bold text-gray-900">{data.revenue.dailyTrafficGoal}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Conversion Rate</h4>
                <p className="text-2xl font-bold text-gray-900">{data.revenue.conversionRate}%</p>
              </div>
            </div>
          </section>

          {/* Commitment */}
          <section>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Commitment</h3>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-800">Word for 2026:</h4>
                  <p className="text-gray-700">{data.commitment.word}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Why:</h4>
                  <p className="text-gray-700">{data.commitment.why}</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => onDownload(gameplan)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
          >
            <SafeIcon icon={FiDownload} />
            Download PDF
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default AdminDashboard;